import { onMounted, onBeforeUnmount } from "vue";
import { isInInput } from "@/helpers/utility/Focus";
import { notNull } from "@/helpers/utility/Type";
import { blobToDataURL } from "@/helpers/utility/ReadFile";

/**
 * Adds copy/paste functionality to the model.
 */
export function useCopyPaste() {
  type CopyableItem = {
    data: object;
    html?: string;
  };
  type Copyfunction = () => CopyableItem | undefined;
  const copyHandlers = new Map<symbol, Copyfunction>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  type PasteFunction = (data: any[]) => void;
  const pasteHandlers = new Map<symbol, PasteFunction>();

  function registerCopyHandler(handler: Copyfunction) {
    const id = Symbol();
    copyHandlers.set(id, handler);
    return () => copyHandlers.delete(id);
  }
  function registerPasteHandler(handler: PasteFunction) {
    const id = Symbol();
    pasteHandlers.set(id, handler);
    return () => pasteHandlers.delete(id);
  }

  function copy(copyableItem: CopyableItem) {
    const clipboardData: {
      [key: string]: Blob;
    } = {};

    if (copyableItem.data == null) {
      console.warn("No data to copy");
      return;
    }
    if (Array.isArray(copyableItem.data) && copyableItem.data.length == 0) {
      console.warn("No data to copy");
      return;
    }

    console.log("copy to clipboard", copyableItem.data);
    clipboardData["text/plain"] = new Blob(
      [JSON.stringify(copyableItem.data)],
      { type: "text/plain" }
    );

    if (copyableItem.html) {
      clipboardData["text/html"] = new Blob([copyableItem.html], {
        type: "text/html"
      });
    }

    // write an HTML version of the items to the clipboard
    try {
      navigator.clipboard.write([new ClipboardItem(clipboardData)]);
    } catch (e) {
      console.warn(e);
    }
  }

  /**
   * Called when the user presses CTRL/CMD + C.
   * Copies the selected items to the clipboard.
   */
  function handleCopyEvent(_event: KeyboardEvent) {
    const copydata = Array.from(copyHandlers.values())
      .map((h) => h())
      .filter(notNull);
    const data = copydata.flatMap((d) => d.data);
    const html = copydata.map((d) => d.html).join("\n");

    copy({ data, html });
  }

  /**
   * Called when the user presses CTRL/CMD + V.
   * Pastes the items from the clipboard.
   * (creates new items, migrates them to the new structure)
   */
  async function handlePasteEvent(_event: KeyboardEvent) {
    try {
      const items = await getClipboardItems();

      // call all paste handlers
      for (const handler of pasteHandlers.values()) {
        handler(items);
      }
    } catch (e) {
      console.warn(e);
    }
  }

  async function getClipboardItems() {
    const clipboardItems = await navigator.clipboard.read();
    const clipboardTextContent = await navigator.clipboard.readText();

    let items = [];

    // Import images directly
    const imageTypes = ["image/png", "image/jpeg", "image/gif", "image/webp", "image/svg+xml"];
    for (const clipboardItem of clipboardItems) {
      for (const type of imageTypes) {
        if (clipboardItem.types.includes(type)) {
          const blob = await clipboardItem.getType(type);
          let height, width;
          try {
            const img = await createImageBitmap(blob);
            height = img.height;
            width = img.width;
          } catch (e) {
            console.warn(e);
            continue;
          }
          const dataUrl = await blobToDataURL(blob);
          items.push({
            type: "image",
            data: dataUrl,
            width: width,
            height: height,
            aspectRatio: width / height,
            mediaType: type,
            createdAt: Date.now()
          });
        }
      }
    }

    if (clipboardTextContent) {
      try {
        const jsonItems = JSON.parse(clipboardTextContent);
        for (const item of jsonItems) {
          items.push(item);
        }
      } catch (e) {
        console.log("Could not parse clipboard content", e, clipboardTextContent);
        items = [{
          type: "text/plain",
          data: clipboardTextContent
        }];
      }
    }

    if (!Array.isArray(items)) items = [items];

    return items;
  }

  /**
   * Handle CTRL/CMD + C/V keydown events.
   */
  function onKeyDown(event: KeyboardEvent) {
    if (event.defaultPrevented) return;

    const isSpecialKeyDown = event.metaKey || event.ctrlKey;
    if (!isSpecialKeyDown) return;

    if (isInInput(event)) return;

    // CTRL/CMD + C
    if (event.key == "c" && copyHandlers.size > 0) {
      event.preventDefault();
      handleCopyEvent(event);
    }

    // CTRL/CMD + V
    if (event.key == "v" && pasteHandlers.size > 0) {
      event.preventDefault();
      handlePasteEvent(event);
    }
  }

  // ======================
  // HOOKS

  onMounted(() => {
    window.addEventListener("keydown", onKeyDown);
  });
  onBeforeUnmount(() => {
    window.removeEventListener("keydown", onKeyDown);
  });

  return {
    onCopyEvent: registerCopyHandler,
    onPasteEvent: registerPasteHandler,
    copy,
    getClipboardItems
  };
}

export function buildHTMLText(item: { id?: number; type?: string }) {
  const data = JSON.stringify(item);

  // prepare json to be inserted into html data attribute
  const encodedData = encodeURIComponent(data);

  const fontFamilyBody =
    "UnitOT-Light, UnitOT, Unit, Segoe UI, Calibri, sans-serif";
  const fontFamilyHeading =
    "UnitOT-Medi, UnitOT, Unit, Segoe UI Semibold, Calibri, sans-serif";

  const container = document.createElement("div");
  if (item.type != null) {
    container.setAttribute("data-type", item.type);
  }
  if (item.id != null) {
    container.setAttribute("data-id", `${item.id}`);
  }
  container.setAttribute("data-data", encodedData);
  container.style.fontFamily = fontFamilyBody;

  return {
    addImage(src: string) {
      const img = document.createElement("img");
      img.src = src;
      container.appendChild(img);
      return this;
    },
    addTitle(title: string, level = 2) {
      const heading = document.createElement(`h${level}`);
      heading.style.fontFamily = fontFamilyHeading;
      heading.style.fontWeight = "500";
      heading.textContent = title;
      container.appendChild(heading);
      return this;
    },
    addText(text: string) {
      const p = document.createElement("p");
      p.textContent = text;
      container.appendChild(p);
      return this;
    },
    addList(items: string[]) {
      const list = document.createElement("ul");
      for (const item of items) {
        const li = document.createElement("li");
        li.textContent = item;
        list.appendChild(li);
      }
      container.appendChild(list);
      return this;
    },
    /**
     * Add HTML (from a string of HTML) to the container
     */
    addHTMLText(htmlText: string) {
      const div = document.createElement("div");
      div.innerHTML = htmlText;
      // add all children to the container
      while (div.firstChild) {
        container.appendChild(div.firstChild);
      }
      return this;
    },
    toString() {
      return container.outerHTML;
    }
  };
}
